/**
 * Shared row renderer for Queue / My Cases / Signed Off tabs.
 * Extracted verbatim from pages/AccountantQueue.tsx during Task 4 — no behavior change.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Lock,
  Unlock,
  CheckCircle,
  AlertTriangle,
  ChevronRight,
  AlertCircle,
  FileText,
  FileSpreadsheet,
  BookOpen,
  FolderOpen,
} from 'lucide-react';
import { CaseSummary } from './types';

type RowMode = 'queue' | 'mine' | 'signed';

interface Props {
  caseItem: CaseSummary;
  mode: RowMode;
  isSelected: boolean;
  onSelect: (id: string) => void;
  currentAccountantId: string;

  // Action handlers (all optional — tab decides which apply)
  onClaim?: (e: React.MouseEvent, c: CaseSummary) => void;
  onRelease?: (e: React.MouseEvent, c: CaseSummary) => void;
  onSignOff?: (e: React.MouseEvent, c: CaseSummary) => void;

  // Per-row spinner state
  claimingCaseId?: string | null;
  releasingCaseId?: string | null;
  signingOffCaseId?: string | null;
}

export default function AccountantCaseRow({
  caseItem,
  mode,
  isSelected,
  onSelect,
  currentAccountantId,
  onClaim,
  onRelease,
  onSignOff,
  claimingCaseId,
  releasingCaseId,
  signingOffCaseId,
}: Props) {
  const navigate = useNavigate();

  // 2026-04-30 (root-cause investigation of "data analyst marks ready, accountant
  // queue doesn't reflect it"): the backend's CaseReadinessService.assess_readiness()
  // is the canonical SSoT for whether a case is ready for accountant sign-off.
  // It surfaces through `_build_accountant_case_summary()` as `is_ready_for_review`
  // on every case in the queue API response. The frontend must honor it as the
  // primary signal — otherwise live cases (like the d6d88aea repro: case_strength=42,
  // assigned_queue='accountant', is_ready_for_review=true) render as "Needs review"
  // even though the backend has positively determined them ready. Legacy heuristics
  // (case_strength>=70, assigned_queue==='ready') kept as additive fallbacks for
  // cases where is_ready_for_review is undefined in older API responses.
  // Regression guard: AccountantCaseRow.readiness.test.tsx
  const canSignOff =
    caseItem.is_ready_for_review === true ||
    caseItem.case_strength >= 70 ||
    caseItem.assigned_queue === 'ready';
  const isMyCase = caseItem.claimed_by_id === currentAccountantId;
  const isClaimed = caseItem.is_claimed || !!caseItem.claimed_by_id;

  // Status dot
  let statusDot = 'var(--color-status-queued)';
  let statusTitle = 'Unknown';
  if (caseItem.assigned_queue === 'ready' || canSignOff) {
    statusDot = 'var(--color-verified)';
    statusTitle = 'Ready for sign-off';
  } else if (caseItem.case_strength >= 50) {
    statusDot = 'var(--color-brand)';
    statusTitle = 'Strong case';
  } else if (caseItem.case_strength >= 30) {
    statusDot = 'var(--color-brand)';
    statusTitle = 'Needs review';
  } else {
    statusDot = 'var(--color-brand)';
    statusTitle = 'Weak - needs attention';
  }

  let actionText = caseItem.next_action || '';
  if (caseItem.next_action_impact) actionText += ` (${caseItem.next_action_impact})`;
  if (!actionText) {
    if (caseItem.readiness_issues && caseItem.readiness_issues.length > 0) {
      actionText = caseItem.readiness_issues[0];
    } else if (canSignOff) {
      actionText = 'Ready for accountant sign-off';
    } else {
      actionText = 'Awaiting AI analysis';
    }
  }

  const progressPct = caseItem.case_strength || 0;

  const eventLabel = caseItem.event_type
    ? caseItem.event_type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : null;

  const displayName = caseItem.business_name || 'Unknown';

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(caseItem.id);
    }
  };

  const showClaim = mode === 'queue' && !isClaimed;
  const showRelease = mode === 'mine' && isMyCase && !canSignOff;
  const showSignOff = mode === 'mine' && canSignOff;
  const showSignedBadge = mode === 'signed';

  return (
    <div
      key={caseItem.id}
      role="button"
      tabIndex={0}
      data-testid={`case-row-${caseItem.id}`}
      aria-label={`${displayName}, ${statusTitle}, ${progressPct}% strength`}
      onKeyDown={handleKeyDown}
      onClick={() => onSelect(caseItem.id)}
      style={isSelected ? { background: 'var(--color-needs-human-tint)', boxShadow: 'inset 0 0 0 1px var(--color-needs-human-tint-bd)' } : undefined}
      className={`
        flex flex-col gap-2 p-4
        md:grid md:grid-cols-12 md:gap-4 md:px-4 md:py-3 md:items-center
        hover:bg-[var(--color-bg-hover)] cursor-pointer transition-colors
      `}
    >
      {/* Mobile compact */}
      <div className="flex items-center gap-3 md:hidden">
        <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: statusDot }} title={statusTitle} />
        <div className="min-w-0 flex-1">
          <div className="font-medium text-[color:var(--color-text-primary)] truncate">{displayName}</div>
          <div className="text-xs text-[color:var(--color-text-muted)]">
            {caseItem.law_firm || 'No law firm'}
            {eventLabel ? ` · ${eventLabel}` : ''}
          </div>
        </div>
        <div className="text-sm font-medium text-[color:var(--color-text-secondary)]">{progressPct}%</div>
      </div>
      <div className="md:hidden text-sm text-[color:var(--color-text-secondary)] pl-5">{actionText}</div>

      {/* Desktop: Case (col-span-3) */}
      <div className="hidden md:flex col-span-3 items-center gap-2 min-w-0 overflow-hidden">
        <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: statusDot }} title={statusTitle} />
        <div className="min-w-0 flex-1 overflow-hidden">
          <div className="font-medium text-[color:var(--color-text-primary)] truncate text-sm leading-tight" data-testid={`case-name-${caseItem.id}`}>
            {displayName}
          </div>
          <div className="text-xs text-[color:var(--color-text-muted)] truncate leading-tight mt-0.5">
            {caseItem.law_firm || 'No law firm'}
          </div>
          <div className="text-[10px] text-[color:var(--color-text-dim)] font-mono leading-tight">#{caseItem.id.slice(0, 8).toUpperCase()}</div>
        </div>
      </div>

      {/* Desktop: Event / Date (col-span-2) */}
      <div className="hidden md:flex col-span-2 flex-col min-w-0 overflow-hidden gap-0.5">
        {eventLabel ? (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[var(--color-bg-elevated)] text-[color:var(--color-text-secondary)] rounded text-[11px] font-medium w-fit max-w-full"
            data-testid={`case-event-${caseItem.id}`}
          >
            <AlertCircle className="w-3 h-3 shrink-0 text-[color:var(--color-text-dim)]" />
            <span className="truncate">{eventLabel}</span>
          </span>
        ) : caseItem.business_type ? (
          <span className="text-xs text-[color:var(--color-text-muted)] truncate">{caseItem.business_type}</span>
        ) : null}
        {caseItem.event_date && (
          <span className="text-[11px] text-[color:var(--color-text-dim)]">
            {new Date(caseItem.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}
          </span>
        )}
      </div>

      {/* Desktop: Status (col-span-3) */}
      <div className="hidden md:flex col-span-3 flex-col min-w-0 overflow-hidden">
        <span className="text-[color:var(--color-text-secondary)] text-xs leading-snug line-clamp-2" title={actionText}>
          {actionText}
        </span>
        {caseItem.pending_classifications ? (
          <span className="inline-flex items-center gap-1 mt-0.5 text-[11px] text-[color:var(--color-needs-human-text)]">
            <AlertTriangle className="w-3 h-3 shrink-0" />
            {caseItem.pending_classifications} pending
          </span>
        ) : null}
        <div className="flex items-center gap-1 mt-1 flex-wrap">
          {/* "Data" badge — green only when both transactions AND anomalies
              are clean. 2026-05-03 follow-up to PR #298: anomalies_count
              is now persisted to db.drafts by /api/v1/anomalies/canonical/with-cache
              when the wizard fires detection with draft_id (see
              _persist_anomaly_count_to_draft in anomaly_detection.py).
              Eventually consistent — refreshed on each Step 3 visit. */}
          {(() => {
            const dataClean =
              (caseItem.transactions_need_review ?? 0) === 0 &&
              (caseItem.anomalies_count ?? 0) === 0;
            return (
              <span
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium"
                style={dataClean ? { background: 'transparent', color: 'var(--color-verified)' } : { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
              >
                {dataClean ? <CheckCircle className="w-2.5 h-2.5" /> : <AlertTriangle className="w-2.5 h-2.5" />}
                Data
              </span>
            );
          })()}
          {/* "Docs" badge — green when backend has positively asserted the
              case is ready for review (the readiness service runs the
              document-completeness check). Pre-2026-05-03 this keyed off
              completion_percentage >= 90, but the backend formula at
              queue_assignment_service._calculate_completion caps near 60
              for fully-uploaded cases (max wizard step + max strength +
              docs = ~100, but realistically wizard step is rarely 5+ when
              docs are done) — so the badge stayed amber forever. The
              canonical signal is is_ready_for_review (computed fresh by
              CaseReadinessService.assess_readiness on every API call). */}
          <span
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium"
            style={caseItem.is_ready_for_review === true ? { background: 'transparent', color: 'var(--color-verified)' } : { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
          >
            {caseItem.is_ready_for_review === true ? <CheckCircle className="w-2.5 h-2.5" /> : <AlertTriangle className="w-2.5 h-2.5" />}
            Docs
          </span>
          <span
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium"
            style={caseItem.is_ready_for_review ? { background: 'transparent', color: 'var(--color-verified)' } : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }}
          >
            {caseItem.is_ready_for_review ? <CheckCircle className="w-2.5 h-2.5" /> : <AlertTriangle className="w-2.5 h-2.5" />}
            Calc
          </span>
        </div>
      </div>

      {/* Desktop: Strength (col-span-1) */}
      <div className="hidden md:flex col-span-1 items-center justify-center">
        <div className="relative w-9 h-9">
          <svg className="w-9 h-9 -rotate-90" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="14" fill="none" style={{ stroke: 'var(--color-border)' }} strokeWidth="3" />
            <circle
              cx="18"
              cy="18"
              r="14"
              fill="none"
              strokeWidth="3"
              style={{ stroke: progressPct >= 70 ? 'var(--color-verified)' : 'var(--color-brand)' }}
              strokeDasharray={`${(progressPct / 100) * 88} 88`}
              strokeLinecap="round"
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-[color:var(--color-text-secondary)]">{progressPct}</span>
        </div>
      </div>

      {/* Desktop: Value (col-span-1) */}
      <div className="hidden md:flex col-span-1 flex-col items-center min-w-0">
        {caseItem.estimated_value > 0 ? (
          <span className="text-xs font-semibold text-[color:var(--color-text-secondary)]" data-testid={`case-value-${caseItem.id}`}>
            {caseItem.estimated_value >= 1000000
              ? `$${(caseItem.estimated_value / 1000000).toFixed(1)}M`
              : caseItem.estimated_value >= 1000
              ? `$${(caseItem.estimated_value / 1000).toFixed(0)}K`
              : `$${caseItem.estimated_value.toFixed(0)}`}
          </span>
        ) : (
          <span className="text-[10px] text-[color:var(--color-text-dim)]">N/A</span>
        )}
        {caseItem.days_stale > 0 && <span className="text-[10px] text-[color:var(--color-text-dim)]">{caseItem.days_stale}d ago</span>}
      </div>

      {/* Desktop: Quick Actions (col-span-2) */}
      <div className="hidden md:flex col-span-2 items-center justify-end gap-0.5">
        {caseItem.wizard_url && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(caseItem.wizard_url!);
            }}
            className="p-1.5 hover:bg-[var(--color-bg-hover)] rounded transition-colors group"
            title="Open in Wizard"
            aria-label={`Open ${displayName} in Wizard`}
            data-testid={`tool-wizard-${caseItem.id}`}
          >
            <FileText className="w-4 h-4 text-[color:var(--color-text-dim)] group-hover:text-[color:var(--color-needs-human-text)]" aria-hidden="true" />
          </button>
        )}
        {/* P&L Workbench — always shown for in-house staff (Clerk-gated route).
            The legacy `reconciliation_url` field is intentionally null for in-house
            accountants (it targets the JWT-only external portal). We hard-code the
            Clerk-gated workbench path instead. */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/dashboard/accountant/workbench/${caseItem.id}`);
          }}
          className="p-1.5 hover:bg-[var(--color-bg-hover)] rounded transition-colors group"
          title="P&L Workbench"
          aria-label={`Open P&L Workbench for ${displayName}`}
          data-testid={`tool-reconciliation-${caseItem.id}`}
        >
          <FileSpreadsheet className="w-4 h-4 text-[color:var(--color-text-dim)] group-hover:text-[color:var(--color-needs-human-text)]" aria-hidden="true" />
        </button>
        {caseItem.narrator_url && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(caseItem.narrator_url!);
            }}
            className="p-1.5 hover:bg-[var(--color-bg-hover)] rounded transition-colors group"
            title="Generate Narrative"
            aria-label={`Generate Narrative for ${displayName}`}
            data-testid={`tool-narrator-${caseItem.id}`}
          >
            <BookOpen className="w-4 h-4 text-[color:var(--color-text-dim)] group-hover:text-[color:var(--color-needs-human-text)]" aria-hidden="true" />
          </button>
        )}
        {caseItem.documents_url && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(caseItem.documents_url!);
            }}
            className="p-1.5 hover:bg-[var(--color-bg-hover)] rounded transition-colors group"
            title="View Documents"
            aria-label={`View Documents for ${displayName}`}
            data-testid={`tool-documents-${caseItem.id}`}
          >
            <FolderOpen className="w-4 h-4 text-[color:var(--color-text-dim)] group-hover:text-[color:var(--color-needs-human-text)]" aria-hidden="true" />
          </button>
        )}
        {showClaim && onClaim && (
          <button
            onClick={(e) => onClaim(e, caseItem)}
            disabled={claimingCaseId === caseItem.id}
            className="ml-1 px-2 py-1 text-[11px] font-medium text-[color:var(--color-on-brand)] bg-[var(--color-brand)] hover:bg-[var(--color-brand-hover)] disabled:opacity-50 rounded transition-colors flex items-center gap-1"
            title="Claim this case"
            data-testid={`tool-claim-${caseItem.id}`}
          >
            <Lock className="w-3 h-3" />
            {claimingCaseId === caseItem.id ? 'Claiming...' : 'Claim'}
          </button>
        )}
        {showRelease && onRelease && (
          <button
            onClick={(e) => onRelease(e, caseItem)}
            disabled={releasingCaseId === caseItem.id}
            className="ml-1 px-2 py-1 text-[11px] font-medium text-[color:var(--color-text-secondary)] bg-[var(--color-bg-elevated)] hover:bg-[var(--color-bg-hover)] disabled:opacity-50 rounded transition-colors flex items-center gap-1"
            title="Release this case"
            data-testid={`tool-release-${caseItem.id}`}
          >
            <Unlock className="w-3 h-3" />
            {releasingCaseId === caseItem.id ? 'Releasing...' : 'Release'}
          </button>
        )}
        {showSignOff && onSignOff && (
          <button
            onClick={(e) => onSignOff(e, caseItem)}
            disabled={signingOffCaseId === caseItem.id}
            className="ml-1 px-2 py-1 text-[11px] font-medium text-[color:var(--color-on-brand)] bg-[var(--color-verified)] hover:bg-[var(--color-verified)] disabled:opacity-50 rounded transition-colors"
            title="Sign off"
            data-testid={`tool-signoff-${caseItem.id}`}
          >
            {signingOffCaseId === caseItem.id ? 'Signing...' : 'Sign Off'}
          </button>
        )}
        {showSignedBadge && (
          <span
            className="ml-1 px-2 py-1 text-[11px] font-medium text-[color:var(--color-verified)] bg-transparent border border-[color:transparent] rounded inline-flex items-center gap-1"
            data-testid={`tool-signed-${caseItem.id}`}
          >
            <CheckCircle className="w-3 h-3" />
            Signed off
          </span>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(caseItem.primary_action_url);
          }}
          className="p-1 hover:bg-[var(--color-bg-hover)] rounded ml-0.5"
          title="Open case"
          aria-label={`Open case ${displayName}`}
        >
          <ChevronRight className="w-4 h-4 text-[color:var(--color-text-dim)]" aria-hidden="true" />
        </button>
      </div>

      {/* Mobile action bar */}
      <div className="flex items-center gap-2 md:hidden pl-5 mt-1">
        {caseItem.wizard_url && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(caseItem.wizard_url!);
            }}
            className="px-2 py-1 text-xs text-[color:var(--color-needs-human-text)] bg-[var(--color-needs-human-tint)] rounded"
          >
            Wizard
          </button>
        )}
        {/* P&L Workbench — always shown (Clerk-gated route, not JWT-dependent) */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/dashboard/accountant/workbench/${caseItem.id}`);
          }}
          className="px-2 py-1 text-xs text-[color:var(--color-needs-human-text)] bg-[var(--color-needs-human-tint)] rounded"
        >
          P&L
        </button>
        {showClaim && onClaim && (
          <button
            onClick={(e) => onClaim(e, caseItem)}
            disabled={claimingCaseId === caseItem.id}
            className="px-2 py-1 text-xs font-medium text-[color:var(--color-on-brand)] bg-[var(--color-brand)] rounded disabled:opacity-50 min-h-[44px]"
          >
            {claimingCaseId === caseItem.id ? 'Claiming...' : 'Claim'}
          </button>
        )}
        {showRelease && onRelease && (
          <button
            onClick={(e) => onRelease(e, caseItem)}
            disabled={releasingCaseId === caseItem.id}
            className="px-2 py-1 text-xs font-medium text-[color:var(--color-text-secondary)] bg-[var(--color-bg-elevated)] rounded disabled:opacity-50 min-h-[44px]"
          >
            {releasingCaseId === caseItem.id ? 'Releasing...' : 'Release'}
          </button>
        )}
        {showSignOff && onSignOff && (
          <button
            onClick={(e) => onSignOff(e, caseItem)}
            disabled={signingOffCaseId === caseItem.id}
            className="px-2 py-1 text-xs font-medium text-[color:var(--color-on-brand)] bg-[var(--color-verified)] rounded disabled:opacity-50 min-h-[44px]"
          >
            {signingOffCaseId === caseItem.id ? 'Signing...' : 'Sign Off'}
          </button>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(caseItem.primary_action_url);
          }}
          className="ml-auto px-3 py-1.5 text-xs font-medium text-[color:var(--color-needs-human-text)] bg-[var(--color-needs-human-tint)] hover:bg-[var(--color-bg-hover)] rounded-lg min-h-[44px]"
        >
          View →
        </button>
      </div>
    </div>
  );
}
